/**
 * ADMIN Company Verification Page
 * ADMIN can view and verify/unverify companies
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiCheckCircle, FiXCircle, FiEye, FiCheck, FiX, FiSearch } from 'react-icons/fi';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { AdminLayout } from '../../components/admin';
import { apiClient } from '../../services/api';
import { useDialog } from '../../hooks/useDialog';
import toast from 'react-hot-toast';

interface Company {
  id: string;
  slug: string;
  nameTh: string;
  nameEn?: string;
  description: string;
  logoUrl?: string;
  phone1: string;
  phone2?: string;
  phone3?: string;
  email?: string;
  website?: string;
  lineId?: string;
  facebookUrl?: string;
  address: string;
  province: string;
  district: string;
  postalCode?: string;
  latitude?: number;
  longitude?: number;
  serviceTypes: string[];
  serviceAreas: string[];
  companySize: 'SMALL' | 'MEDIUM' | 'LARGE';
  establishedYear?: number;
  licenseNumber?: string;
  registrationId?: string;
  taxId?: string;
  isFeatured: boolean;
  isActive: boolean;
  isVerified: boolean;
  verifiedAt?: string;
  viewCount: number;
  contactCount: number;
  createdAt: string;
  updatedAt: string;
}

interface CompaniesResponse {
  companies: Company[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

const CompanyVerificationPage: React.FC = () => {
  const { showConfirm, ConfirmDialog, AlertDialog } = useDialog();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [verifiedFilter, setVerifiedFilter] = useState<string>('UNVERIFIED');
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [provinceFilter, setProvinceFilter] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const perPage = 20;

  useEffect(() => {
    loadCompanies();
  }, [verifiedFilter, currentPage, searchTerm, provinceFilter]);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: currentPage,
        per_page: perPage,
      };

      // Filter by verification status
      if (verifiedFilter === 'VERIFIED') {
        params.is_verified = true;
      } else if (verifiedFilter === 'UNVERIFIED') {
        params.is_verified = false;
      }
      // 'ALL' = no filter

      // Search term (Backend uses 'query' parameter, not 'search')
      if (searchTerm.trim()) {
        params.query = searchTerm.trim();
      }

      // Province filter
      if (provinceFilter.trim()) {
        params.province = provinceFilter.trim();
      }

      const response = await apiClient.get<CompaniesResponse>('/api/admin/companies', { params });
      setCompanies(response.data.companies || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
    } catch (error: any) {
      console.error('Failed to load companies:', error);
      toast.error('ไม่สามารถโหลดข้อมูลบริษัทได้');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = (company: Company) => {
    setSelectedCompany(company);
    setShowDetailModal(true);
  };

  const handleVerify = async (companyId: string) => {
    await showConfirm(
      {
        title: 'ยืนยันการตรวจสอบบริษัท',
        message: 'คุณต้องการยืนยันบริษัทนี้ใช่หรือไม่?',
        confirmText: 'ยืนยัน',
        cancelText: 'ยกเลิก',
        variant: 'success'
      },
      async () => {
        setActionLoading(true);
        try {
          await apiClient.post(`/api/admin/companies/${companyId}/verify`);
          toast.success('ยืนยันบริษัทสำเร็จ');
          setShowDetailModal(false);
          await loadCompanies();
        } catch (error: any) {
          console.error('Failed to verify company:', error);
          toast.error(error.response?.data?.message || 'ไม่สามารถยืนยันบริษัทได้');
        } finally {
          setActionLoading(false);
        }
      }
    );
  };

  const handleUnverify = async (companyId: string) => {
    await showConfirm(
      {
        title: 'ยกเลิกการยืนยันบริษัท',
        message: 'คุณต้องการยกเลิกการยืนยันบริษัทนี้ใช่หรือไม่?',
        confirmText: 'ยกเลิกการยืนยัน',
        cancelText: 'ยกเลิก',
        variant: 'warning'
      },
      async () => {
        setActionLoading(true);
        try {
          await apiClient.post(`/api/admin/companies/${companyId}/unverify`);
          toast.success('ยกเลิกการยืนยันบริษัทสำเร็จ');
          setShowDetailModal(false);
          await loadCompanies();
        } catch (error: any) {
          console.error('Failed to unverify company:', error);
          toast.error(error.response?.data?.message || 'ไม่สามารถยกเลิกการยืนยันบริษัทได้');
        } finally {
          setActionLoading(false);
        }
      }
    );
  };

  const getVerificationBadge = (isVerified: boolean) => {
    if (isVerified) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <FiCheckCircle className="mr-1" /> ยืนยันแล้ว
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        <FiXCircle className="mr-1" /> ยังไม่ยืนยัน
      </span>
    );
  };

  const getCompanySizeBadge = (size: string) => {
    const sizeMap: Record<string, { label: string; color: string }> = {
      SMALL: { label: 'เล็ก', color: 'bg-blue-100 text-blue-800' },
      MEDIUM: { label: 'กลาง', color: 'bg-purple-100 text-purple-800' },
      LARGE: { label: 'ใหญ่', color: 'bg-orange-100 text-orange-800' },
    };
    const info = sizeMap[size] || { label: size, color: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded ${info.color}`}>
        {info.label}
      </span>
    );
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    loadCompanies();
  };

  if (loading && companies.length === 0) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">✅ การจัดการการยืนยันบริษัท</h1>
          <p className="text-gray-600">จัดการการยืนยันบริษัททั้งหมด (ทั้งหมด {total} รายการ)</p>
        </div>

        {/* Search & Filters */}
        <div className="mb-6 space-y-4">
          {/* Search Form */}
          <form onSubmit={handleSearch} className="flex space-x-2">
            <div className="flex-1">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="ค้นหาชื่อบริษัท..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <input
              type="text"
              value={provinceFilter}
              onChange={(e) => setProvinceFilter(e.target.value)}
              placeholder="จังหวัด"
              className="w-48 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <Button type="submit" variant="default">
              ค้นหา
            </Button>
          </form>

          {/* Verification Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">สถานะการยืนยัน</label>
            <div className="flex space-x-2">
              <Button
                variant={verifiedFilter === 'UNVERIFIED' ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setVerifiedFilter('UNVERIFIED');
                  setCurrentPage(1);
                }}
              >
                ยังไม่ยืนยัน
              </Button>
              <Button
                variant={verifiedFilter === 'VERIFIED' ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setVerifiedFilter('VERIFIED');
                  setCurrentPage(1);
                }}
              >
                ยืนยันแล้ว
              </Button>
              <Button
                variant={verifiedFilter === 'ALL' ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setVerifiedFilter('ALL');
                  setCurrentPage(1);
                }}
              >
                ทั้งหมด
              </Button>
            </div>
          </div>
        </div>

        {/* Companies Table */}
        <Card>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ชื่อบริษัท</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">จังหวัด</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ขนาดบริษัท</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">สถานะยืนยัน</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">สถิติ</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">การจัดการ</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {companies.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      ไม่พบบริษัท
                    </td>
                  </tr>
                ) : (
                  companies.map(company => (
                    <tr key={company.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{company.nameTh}</div>
                        {company.nameEn && (
                          <div className="text-xs text-gray-500">{company.nameEn}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{company.province}</div>
                        {company.district && (
                          <div className="text-xs text-gray-500">{company.district}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getCompanySizeBadge(company.companySize)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getVerificationBadge(company.isVerified)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center space-x-2">
                          <span title="จำนวนครั้งที่เข้าชม">{company.viewCount} views</span>
                          <span className="text-gray-300">|</span>
                          <span title="จำนวนครั้งที่ติดต่อ">{company.contactCount} contacts</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          {company.isVerified ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleUnverify(company.id)}
                              className="text-yellow-600 border-yellow-600 hover:bg-yellow-50"
                            >
                              <FiX className="mr-1" /> ยกเลิกการยืนยัน
                            </Button>
                          ) : (
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => handleVerify(company.id)}
                              className="bg-green-600 hover:bg-green-700 text-white"
                            >
                              <FiCheck className="mr-1" /> ยืนยัน
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDetail(company)}
                          >
                            <FiEye className="mr-1" /> ดูรายละเอียด
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                หน้า {currentPage} จาก {totalPages} (ทั้งหมด {total} รายการ)
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  ก่อนหน้า
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  ถัดไป
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* Detail Modal */}
        {showDetailModal && selectedCompany && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-50"
              onClick={() => setShowDetailModal(false)}
            />

            {/* Modal */}
            <div className="flex min-h-screen items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="relative w-full max-w-4xl"
              >
                <Card className="p-6 max-h-[90vh] overflow-y-auto">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">{selectedCompany.nameTh}</h2>
                      {selectedCompany.nameEn && (
                        <p className="text-sm text-gray-600 mt-1">{selectedCompany.nameEn}</p>
                      )}
                    </div>
                    <button
                      onClick={() => setShowDetailModal(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <FiX className="w-6 h-6" />
                    </button>
                  </div>

                  {/* Status */}
                  <div className="mb-6 flex items-center space-x-4">
                    <span className="text-sm font-medium text-gray-500">สถานะ:</span>
                    {getVerificationBadge(selectedCompany.isVerified)}
                    {selectedCompany.isVerified && selectedCompany.verifiedAt && (
                      <span className="text-xs text-gray-500">
                        ยืนยันเมื่อ: {new Date(selectedCompany.verifiedAt).toLocaleDateString('th-TH')}
                      </span>
                    )}
                  </div>

                  {/* Company Info Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    {/* Basic Info */}
                    <div className="space-y-3">
                      <h3 className="text-lg font-semibold text-gray-900">ข้อมูลพื้นฐาน</h3>
                      <div>
                        <span className="text-sm font-medium text-gray-500">ที่อยู่:</span>
                        <p className="text-sm text-gray-900 mt-1">{selectedCompany.address}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-500">จังหวัด/อำเภอ:</span>
                        <p className="text-sm text-gray-900 mt-1">
                          {selectedCompany.province} {selectedCompany.district && `/ ${selectedCompany.district}`}
                        </p>
                      </div>
                      {selectedCompany.postalCode && (
                        <div>
                          <span className="text-sm font-medium text-gray-500">รหัสไปรษณีย์:</span>
                          <p className="text-sm text-gray-900 mt-1">{selectedCompany.postalCode}</p>
                        </div>
                      )}
                      <div>
                        <span className="text-sm font-medium text-gray-500">ขนาดบริษัท:</span>
                        <p className="text-sm text-gray-900 mt-1">{getCompanySizeBadge(selectedCompany.companySize)}</p>
                      </div>
                      {selectedCompany.establishedYear && (
                        <div>
                          <span className="text-sm font-medium text-gray-500">ปีก่อตั้ง:</span>
                          <p className="text-sm text-gray-900 mt-1">{selectedCompany.establishedYear}</p>
                        </div>
                      )}
                    </div>

                    {/* Contact Info */}
                    <div className="space-y-3">
                      <h3 className="text-lg font-semibold text-gray-900">ข้อมูลติดต่อ</h3>
                      <div>
                        <span className="text-sm font-medium text-gray-500">โทรศัพท์:</span>
                        <p className="text-sm text-gray-900 mt-1">
                          {selectedCompany.phone1}
                          {selectedCompany.phone2 && `, ${selectedCompany.phone2}`}
                          {selectedCompany.phone3 && `, ${selectedCompany.phone3}`}
                        </p>
                      </div>
                      {selectedCompany.email && (
                        <div>
                          <span className="text-sm font-medium text-gray-500">อีเมล:</span>
                          <p className="text-sm text-gray-900 mt-1">{selectedCompany.email}</p>
                        </div>
                      )}
                      {selectedCompany.website && (
                        <div>
                          <span className="text-sm font-medium text-gray-500">เว็บไซต์:</span>
                          <p className="text-sm text-gray-900 mt-1">
                            <a href={selectedCompany.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                              {selectedCompany.website}
                            </a>
                          </p>
                        </div>
                      )}
                      {selectedCompany.lineId && (
                        <div>
                          <span className="text-sm font-medium text-gray-500">Line ID:</span>
                          <p className="text-sm text-gray-900 mt-1">{selectedCompany.lineId}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  {selectedCompany.description && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">รายละเอียด</h3>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg">
                        {selectedCompany.description}
                      </p>
                    </div>
                  )}

                  {/* Service Types */}
                  {selectedCompany.serviceTypes && selectedCompany.serviceTypes.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">ประเภทบริการ</h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedCompany.serviceTypes.map((type, index) => (
                          <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                            {type}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Service Areas */}
                  {selectedCompany.serviceAreas && selectedCompany.serviceAreas.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">พื้นที่ให้บริการ</h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedCompany.serviceAreas.map((area, index) => (
                          <span key={index} className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full">
                            {area}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Legal Info */}
                  {(selectedCompany.licenseNumber || selectedCompany.registrationId || selectedCompany.taxId) && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">ข้อมูลทางกฎหมาย</h3>
                      <div className="space-y-2 bg-gray-50 p-4 rounded-lg">
                        {selectedCompany.licenseNumber && (
                          <div>
                            <span className="text-sm font-medium text-gray-500">เลขใบอนุญาต:</span>
                            <span className="ml-2 text-sm text-gray-900">{selectedCompany.licenseNumber}</span>
                          </div>
                        )}
                        {selectedCompany.registrationId && (
                          <div>
                            <span className="text-sm font-medium text-gray-500">เลขทะเบียนบริษัท:</span>
                            <span className="ml-2 text-sm text-gray-900">{selectedCompany.registrationId}</span>
                          </div>
                        )}
                        {selectedCompany.taxId && (
                          <div>
                            <span className="text-sm font-medium text-gray-500">เลขประจำตัวผู้เสียภาษี:</span>
                            <span className="ml-2 text-sm text-gray-900">{selectedCompany.taxId}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Statistics */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">สถิติ</h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <div className="text-sm text-gray-600">จำนวนผู้เข้าชม</div>
                        <div className="text-2xl font-bold text-blue-600">{selectedCompany.viewCount}</div>
                      </div>
                      <div className="bg-green-50 p-3 rounded-lg">
                        <div className="text-sm text-gray-600">จำนวนติดต่อ</div>
                        <div className="text-2xl font-bold text-green-600">{selectedCompany.contactCount}</div>
                      </div>
                      <div className="bg-purple-50 p-3 rounded-lg">
                        <div className="text-sm text-gray-600">สถานะ</div>
                        <div className="text-sm font-medium mt-1">
                          {selectedCompany.isActive ? (
                            <span className="text-green-600">ใช้งาน</span>
                          ) : (
                            <span className="text-red-600">ไม่ใช้งาน</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                    {selectedCompany.isVerified ? (
                      <Button
                        variant="destructive"
                        onClick={() => handleUnverify(selectedCompany.id)}
                        disabled={actionLoading}
                        leftIcon={<FiX />}
                        loading={actionLoading}
                        loadingText="กำลังดำเนินการ..."
                      >
                        ยกเลิกการยืนยัน
                      </Button>
                    ) : (
                      <Button
                        variant="success"
                        onClick={() => handleVerify(selectedCompany.id)}
                        disabled={actionLoading}
                        leftIcon={<FiCheck />}
                        loading={actionLoading}
                        loadingText="กำลังดำเนินการ..."
                      >
                        ยืนยันบริษัท
                      </Button>
                    )}
                  </div>
                </Card>
              </motion.div>
            </div>
          </div>
        )}

        {/* Dialog Components */}
        {ConfirmDialog}
        {AlertDialog}
      </div>
    </AdminLayout>
  );
};

export default CompanyVerificationPage;
